import { Slider as DefaultSlider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { readPlatformThemeFromDocument } from '@helpers/platformTheme.helper';
import React, { useMemo } from 'react';
import styles from './index.module.scss';

interface CustomizedSliderProps {
  colorCode?: string;
  backgroundColor?: string;
}

const CustomizedSlider = styled(DefaultSlider)<CustomizedSliderProps>(
  ({ colorCode = '#019DF2', backgroundColor = '#019DF2' }) => ({
    color: colorCode,
    height: 8,
    '& .MuiSlider-track': {
      border: 'none',
      backgroundColor: `${colorCode} !important`,
    },
    '& .MuiSlider-thumb': {
      backgroundColor: `${colorCode} !important`,
    },
    '& .MuiSlider-markActive': {
      backgroundColor: `${colorCode} !important`,
    },
    '& .MuiSlider-valueLabel': {
      lineHeight: 1.2,
      fontSize: 12,
      background: 'unset',
      padding: 0,
      width: 32,
      height: 32,
      borderRadius: '50% 50% 50% 0',
      backgroundColor,
      transformOrigin: 'bottom left',
      transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
      '&::before': { display: 'none' },
      '&.MuiSlider-valueLabelOpen': {
        transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
      },
      '& > *': {
        transform: 'rotate(45deg)',
      },
    },
  }),
);

export const Slider: React.FC<SliderProps & CustomizedSliderProps> = (props) => {
  const {
    sliderValue,
    handleSliderChange,
    defaultValue,
    step,
    marks,
    minValue,
    maxValue,
    valueLabel,
    className,
    colorCode,
    backgroundColor,
    id,
    onKeyDown,
    role,
    tabIndex,
  } = props;

  const platformTheme = useMemo(() => readPlatformThemeFromDocument(), []);
  const resolvedColorCode = colorCode ?? platformTheme.primaryCta;
  const resolvedBackgroundColor = backgroundColor ?? platformTheme.primaryCta;

  return (
    <div className={className}>
      <CustomizedSlider
        id={id}
        aria-label="Small steps"
        defaultValue={defaultValue}
        step={step}
        marks={marks}
        min={minValue}
        max={maxValue}
        value={sliderValue}
        onChange={handleSliderChange}
        valueLabelDisplay={valueLabel}
        valueLabelFormat={(value: number) => `${value}`}
        colorCode={resolvedColorCode}
        backgroundColor={resolvedBackgroundColor}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role={role}
      />
    </div>
  );
};

Slider.defaultProps = {
  className: styles.root,
  minValue: 0,
  maxValue: 10,
  sliderValue: 7,
  step: 1,
  defaultValue: 7,
  valueLabel: 'on',
  colorCode: undefined,
  backgroundColor: undefined,
  id: 'slider',
  onKeyDown: () => null,
  tabIndex: 0,
  role: 'slider',
};

interface SliderProps {
  sliderValue?: number;
  handleSliderChange: any;
  defaultValue?: number;
  step?: number;
  marks: boolean;
  minValue?: number;
  maxValue?: number;
  valueLabel?: 'on' | 'off' | 'auto';
  className?: string;
  id?: any;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  tabIndex?: number;
  role?: string;
}
